import React from "react";
import { Loader2 } from "lucide-react";

import {
  BROKER_FIELD_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { DashboardLayoutDialogFooter } from "@/views/Dashboard/Dashboard/components/DashboardLayoutDialogFooter";

export type EditBrokerAccountNameTarget = {
  accountCode: string;
  displayName: string;
};

export interface EditBrokerAccountNameDialogProps {
  target: EditBrokerAccountNameTarget | null;
  value: string;
  onValueChange: (value: string) => void;
  isSaving: boolean;
  onCancel: () => void;
  onSave: () => void;
}

export function EditBrokerAccountNameDialog({
  target,
  value,
  onValueChange,
  isSaving,
  onCancel,
  onSave,
}: EditBrokerAccountNameDialogProps) {
  const handleOpenChange = (open: boolean) => {
    if (!open && !isSaving) onCancel();
  };

  return (
    <StrategyPineDetailDialog
      open={!!target}
      onOpenChange={handleOpenChange}
      title="Edit account name"
      description={
        target
          ? `Set a custom display name for ${target.displayName}. Leave blank to use the broker account code.`
          : undefined
      }
      contentClassName="sm:max-w-md"
      bodyClassName="flex-none overflow-visible"
      showDismissButton={false}
      footer={
        <DashboardLayoutDialogFooter onCancel={onCancel}>
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="shrink-0"
            disabled={isSaving}
            aria-busy={isSaving || undefined}
            onClick={onSave}
          >
            {isSaving ? (
              <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
            ) : (
              <span data-font="primary">Save</span>
            )}
          </Button>
        </DashboardLayoutDialogFooter>
      }
    >
      {target ? (
        <Field className="gap-2">
          <FieldLabel htmlFor="broker-account-custom-name" className={BROKER_ALERT_TITLE_CLASS}>
            Display name
          </FieldLabel>
          <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
            Account code: {target.accountCode}
          </FieldDescription>
          <Input
            id="broker-account-custom-name"
            value={value}
            onChange={(event) => onValueChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !isSaving) onSave();
              if (event.key === "Escape" && !isSaving) onCancel();
            }}
            placeholder="Custom display name"
            autoFocus
            disabled={isSaving}
            data-font="primary"
          />
        </Field>
      ) : null}
    </StrategyPineDetailDialog>
  );
}
